import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useConditions from '../conditions';
import CONDITION_TYPES from '../conditions/types';
import useUser from '../user';
import { keyUpdatedAction } from '../../redux/actions/local';
import { getFormValues } from '../actions/steps/utils';

/*
  Tests all validations setup on a page. The data that is used when processing each validation is retrieved by looking at the form fields on the page
*/
const useValidations = () => {
  const [valid, setValid] = useState(false);
  const [processed, setProcessed] = useState(false);
  const [validations, setValidations] = useState([]); // The validations that this element is interested in
  const { evaluate } = useConditions();
  const { emailIsUnique } = useUser();
  const { documents, selections } = useSelector(state => state.DataLinks);
  const { user, firebaseUser } = useSelector(state => state.User);
  const local = useSelector(state => state.Local);

  const dispatch = useDispatch();

  const validate = async ({
    page,
    data,
    formId,
    htmlElement,
    preventDispatch = false
  }) => {
    let validations = page?.validations || [];
    validations.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));
    let validationResults = { ...local?.validations };
    if (htmlElement?.id) {
      validations = validations.filter(v => v.field === htmlElement.id);
    }

    let parentFormId = formId;
    if (!parentFormId && htmlElement.id) {
      parentFormId = findFormId(htmlElement.id);
      if (!parentFormId) {
        return {
          error: 'a parent form could not be found for the specified element id'
        };
      }
    }

    let inputs = getFormValues({ formId: parentFormId });

    if (htmlElement) {
      inputs = inputs.filter(
        input => input.id?.toLowerCase() === htmlElement.id?.toLowerCase()
      );
    }

    if (validations?.length > 0) {
      const results = await Promise.all(
        page.validations
          .filter(validation =>
            inputs.find(input => input.id === validation.field)
          )
          .map(async validation => {
            let result;
            if (validation.validationType === 'function') {
              result = await computeFunction(validation, data, inputs);
            } else {
              delete validationResults[validation.field];

              const condition = {
                conditionType: CONDITION_TYPES.VALIDATION,
                conditionValidationDataLinkId: validation.dataLinkId,
                conditionValidationField: validation.field,
                conditionValidationFieldType: validation.fieldType,
                conditionValidationOperator: validation.validationType,
                conditionValidationComparisionValue: validation.value,
                conditionValidationValue: inputs.find(
                  input => input.id === validation.field
                ).value,
                conditionValidationContentType: validation.contentTypeName
              };

              if (validation.validationType === 'emailUnique') {
                result = await emailIsUnique(
                  condition.conditionValidationValue
                );
              } else {
                result = await evaluate({
                  condition,
                  documents,
                  selections,
                  local
                });
              }
            }

            return {
              validation,
              value: inputs.find(input => input.id === validation.field)?.value,
              valid:
                (result?.matched === true || result === true) && !result.error
            };
          })
      );

      const valid = results.every(r => r.valid === true);

      results.forEach(result => {
        /* 
          Have we already set this field to invalid? 
            i.e. the same field may have multiple validations
          This means that we will always return the first error message
        */
        if (!(validationResults[result.validation.field]?.error === true)) {
          validationResults[result.validation.field] = {
            error: !result.valid,
            value: result.value,
            message: result.valid
              ? result.validation.validatedMessage
              : result.validation.errorMessage
          };
        }
      });

      if (!validationResults.notValidated) {
        validationResults.notValidated = {};
      }

      inputs
        .filter(input => !page.validations.find(v => input.id === v.field))
        .forEach(input => {
          validationResults.notValidated[input.id] = input;
        });

      if (!preventDispatch) {
        dispatch(keyUpdatedAction('validations', validationResults));
      }

      if (htmlElement) {
        if (htmlElement.validationSource === 'validation') {
          // bind to a particular validation
          const validation = results.find(
            r => r.validation.id === htmlElement.validationId
          );
          setValid(validation.valid);
          setValidations([validation]);
        } else if (htmlElement.validationSource === 'field') {
          // bind to any message on a particular field
          const applicableValidations = results
            .filter(
              r =>
                r.validation.field === htmlElement.sourceField &&
                r.validation.dataLinkId === htmlElement.dataLinkId
            )
            .sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));

          setValid(applicableValidations.every(validation => validation.valid));
          setValidations(applicableValidations);
        }
      } else {
        // htmlElement was not supplied so we are interested in whether they all passed
        setValid(valid);
        setValidations(results);
      }

      setProcessed(true);
    } else {
      if (!preventDispatch) {
        dispatch(
          keyUpdatedAction({
            validations: {}
          })
        );
      }
      setValid(true);
      setProcessed(true);
    }

    let hasError = false;
    Object.keys(validationResults).forEach(field => {
      if (validationResults[field].error === true) {
        hasError = true;
      }
    });

    return {
      error: hasError,
      validations: validationResults
    };
  };

  const findFormId = elementId => {
    // Get the element with the given id
    var element = document.getElementById(elementId);

    // Traverse upwards until we find a <form> element or reach the top of the document
    while (element && element.tagName !== 'FORM') {
      element = element.parentNode;
    }

    // If a <form> element is found, return its id; otherwise, return null
    return element ? element.id : null;
  };

  /*
  useEffect(() => {
    if (page) {
      validate(page, htmlElement);
    }
  }, [page, documents]);
  */

  const computeFunction = async (validation, data, inputs) => {
    //eslint-disable-next-line
    const computeFunc = new Function(
      'documents',
      'selectedDocument',
      'selections',
      'user',
      'firebaseUser',
      'local',
      'data',
      'inputs',
      validation.function
    );

    let document;
    if (
      documents[validation.dataLinkId] &&
      documents[validation.dataLinkId].documents.length > 0 &&
      selections &&
      selections[validation.dataLinkId]
    ) {
      document = documents[validation.dataLinkId].documents.find(
        d => d.id === selections[validation.dataLinkId].documentId
      );
    }

    try {
      //documents,selectedDocument,selections,user,local
      const value = computeFunc(
        documents[validation.dataLinkId]?.documents,
        document,
        selections,
        user,
        firebaseUser,
        local,
        data,
        inputs
      );

      return value;
    } catch (error) {
      console.error('useValidations.computeFunction', error);
      return false;
    }
  };

  return {
    processed,
    valid,
    validations,
    validate
  };
};

export default useValidations;
